import { useEffect, useId, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SEARCH_LIMIT, useClientSearch } from '../api/search.ts';
import { LoadFailure, isSessionFailure } from './LoadFailure.tsx';
import type { ClientListRow } from '../api/types.ts';

const DEBOUNCE_MS = 250;

/**
 * Find a client from anywhere: the top bar's typeahead.
 *
 * Searches as you type, a beat after the last keystroke, over the same
 * `GET /clients` the client list uses, so a former name finds the client as it
 * does there. Arrow keys move through the matches, Enter opens the highlighted
 * one, Escape closes the list.
 *
 * **A failed search never reads as "no matches".** That is the bug the client
 * list already had and lost: a refused session rendered as an empty list, a
 * false and calm statement about the data. So:
 *
 *  - a 401, or a 403 from the authenticator, says the session is not valid;
 *  - any other failure says the search could not be run;
 *  - "No matches" appears only for a successful answer, to this exact text,
 *    with zero rows.
 *
 * The previous matches stay on screen while the next search runs, so the list
 * does not blank on every keystroke; but they are the previous text's matches,
 * so Enter will not open one until the current search has answered, and a
 * failure takes the panel even with them in hand.
 */
export function ClientSearch({ className = '' }: { className?: string }) {
  const navigate = useNavigate();
  const listId = useId();
  const box = useRef<HTMLDivElement>(null);
  const [typed, setTyped] = useState('');
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => setQ(typed.trim()), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [typed]);

  useEffect(() => setActive(0), [q]);

  // Close on a press anywhere outside, not on blur: a blur fires before the
  // click on a match lands, and would close the list out from under it.
  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  const search = useClientSearch(q);
  const text = typed.trim();
  const failure = search.error ?? (search.isPlaceholderData ? search.failureReason : null);
  const takeover =
    failure !== null && (isSessionFailure(failure) || !search.data || search.isPlaceholderData);
  // An answer to what is in the box now, not to what was in it a moment ago.
  const answered = q !== '' && q === text && search.isSuccess && !search.isPlaceholderData;
  const rows: ClientListRow[] = takeover || q === '' ? [] : search.data?.clients ?? [];
  const total = search.data?.total ?? 0;

  const openClient = (row: ClientListRow) => {
    setOpen(false);
    setTyped('');
    setQ('');
    // The typeahead can be used from anywhere, so the label names wherever that
    // was rather than guessing: back is still back, it just says "Back".
    navigate(`/clients/${row.client_id}`, { state: {} });
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      if (rows.length > 0) setActive((i) => Math.min(i + 1, rows.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (answered && rows[active]) openClient(rows[active]);
      // Typed faster than the debounce: run the search now rather than open a
      // match for text that is no longer in the box.
      else if (text !== q) setQ(text);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  const showPanel = open && text !== '';
  const optionId = (i: number) => `${listId}-option-${i}`;

  return (
    <div ref={box} className={`relative ${className}`}>
      <label className="sr-only" htmlFor={`${listId}-input`}>
        Find a client by name, reference or former name
      </label>
      <input
        id={`${listId}-input`}
        type="search"
        role="combobox"
        aria-expanded={showPanel}
        aria-controls={`${listId}-list`}
        aria-autocomplete="list"
        aria-activedescendant={showPanel && answered && rows[active] ? optionId(active) : undefined}
        autoComplete="off"
        inputMode="search"
        value={typed}
        placeholder="Find a client"
        onChange={(e) => {
          setTyped(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        className="min-h-[44px] w-full rounded-lg border border-neutral-300 bg-white px-3 text-base
                   text-neutral-900"
      />

      {showPanel && (
        <div
          id={`${listId}-list`}
          className="absolute top-full right-0 left-0 z-30 mt-1 max-h-[70vh] overflow-y-auto rounded-lg
                     border border-neutral-200 bg-white shadow-lg"
        >
          {failure && takeover ? (
            <LoadFailure
              what="The search"
              verb="could not be run"
              error={failure}
              onRetry={() => void search.refetch()}
              retrying={search.isFetching}
            />
          ) : answered && rows.length === 0 ? (
            <p className="px-3 py-3 text-[0.9375rem] text-neutral-600">
              No matches for “{q}”. Former names are searched too.
            </p>
          ) : rows.length === 0 ? (
            <p className="px-3 py-3 text-[0.9375rem] text-neutral-500">Searching…</p>
          ) : (
            <>
              {!answered && <p className="px-3 pt-2 text-sm text-neutral-500">Searching…</p>}
              <ul role="listbox" aria-label="Matching clients" className="py-1">
                {rows.map((row, i) => (
                  <li
                    key={row.client_id}
                    id={optionId(i)}
                    role="option"
                    aria-selected={answered && i === active}
                    onMouseDown={(e) => e.preventDefault()}
                    onMouseEnter={() => setActive(i)}
                    onClick={() => openClient(row)}
                    className={`flex min-h-[44px] cursor-pointer items-center gap-3 px-3 py-2 ${
                      answered && i === active ? 'bg-neutral-100' : ''
                    } ${answered ? '' : 'opacity-60'}`}
                  >
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-semibold text-neutral-900">{row.display_name}</p>
                      <p className="truncate text-sm text-neutral-600">
                        {row.whereabouts_unknown ? 'Whereabouts unknown' : row.where_to_find_them ?? 'Not recorded'}
                      </p>
                    </div>
                    {row.critical_flags > 0 && (
                      <span className="shrink-0 rounded bg-critical-bg px-2 py-0.5 text-xs font-bold tracking-wide
                                       text-critical-fg uppercase">
                        Critical
                      </span>
                    )}
                    <span className="shrink-0 font-mono text-sm text-neutral-500 tabular-nums">{row.client_ref}</span>
                  </li>
                ))}
              </ul>
              {answered && total > rows.length && (
                <p className="border-t border-neutral-200 px-3 py-2 text-sm text-neutral-500">
                  Showing the first {SEARCH_LIMIT} of {total}. Keep typing to narrow it.
                </p>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
